import { useState } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProviderRepositories, useProviderRepoBranches, useProviderRepoFiles } from '@/hooks'
import type { ConnectorType } from '@/types'
import type { RepoPromptOverride } from '@/types/claude'
import { RepoSearchCombo } from './RepoSearchCombo'
import { REPO_CONNECTOR_TYPES } from './repoConnectorTypes'
import { normalizeFolder, parseAccept, matchesAccept } from './repoFilePickerUtils'

const FIELD_LABEL_CLASS = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-foreground'

interface Props {
  projectId: string
  connectorTypes: ConnectorType[]
  value: RepoPromptOverride
  onChange: (next: RepoPromptOverride) => void
  testIdPrefix?: string
  folderPrefix?: string
  accept?: string
  fileLabel?: string
}

export function RepoPromptPicker({ projectId, connectorTypes, value, onChange, testIdPrefix = 'repo-prompt', folderPrefix, accept, fileLabel = 'Prompt file' }: Readonly<Props>) {
  const available = connectorTypes.filter(t => REPO_CONNECTOR_TYPES.includes(t))
  const connectorType = (value.connectorType || available[0]) as ConnectorType
  const connectorId = `${testIdPrefix}-connector`

  if (available.length === 0)
    return (
      <p className="text-xs text-amber-700">
        No repository connector is configured for this project. Connect one to use a prompt from a repository.
      </p>
    )

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={connectorId} className={FIELD_LABEL_CLASS}>Connector</Label>
          <Select
            value={connectorType}
            onValueChange={v => onChange({ connectorType: v, repository: '', branch: undefined, filePath: '' })}
          >
            <SelectTrigger id={connectorId} className="h-8 cursor-pointer" data-testid={connectorId}><SelectValue /></SelectTrigger>
            <SelectContent>
              {available.map(t => (
                <SelectItem key={t} value={t} className="cursor-pointer">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <RepositoryCombo
          projectId={projectId}
          connectorType={connectorType}
          value={value.repository || ''}
          onChange={repository => onChange(repository === (value.repository || '') ? value : { ...value, repository, branch: undefined, filePath: '' })}
          testId={`${testIdPrefix}-repository`}
        />
      </div>

      {value.repository && (
        <div className="grid grid-cols-2 gap-2">
          <BranchCombo
            projectId={projectId}
            connectorType={connectorType}
            repository={value.repository}
            value={value.branch ?? ''}
            onChange={branch => {
              const next = branch || undefined
              onChange({ ...value, branch: next, filePath: next === value.branch ? value.filePath : '' })
            }}
            testId={`${testIdPrefix}-branch`}
          />
          <FileCombo
            projectId={projectId}
            connectorType={connectorType}
            repository={value.repository}
            branch={value.branch ?? ''}
            value={value.filePath}
            onChange={filePath => onChange({ ...value, filePath })}
            testId={`${testIdPrefix}-file`}
            folderPrefix={folderPrefix}
            accept={accept}
            label={fileLabel}
          />
        </div>
      )}
    </div>
  )
}

function RepositoryCombo({ projectId, connectorType, value, onChange, testId }: {
  projectId: string
  connectorType: ConnectorType
  value: string
  onChange: (repository: string) => void
  testId: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const { data: repositories = [], isLoading } = useProviderRepositories(projectId, connectorType, open, query)

  return (
    <RepoSearchCombo
      id={testId}
      testId={testId}
      label="Repository"
      placeholder="Search repository"
      value={value}
      onChange={onChange}
      options={repositories.map(r => ({ value: r.fullName, label: r.fullName }))}
      isLoading={isLoading}
      loadingText="Searching repositories..."
      emptyText="No matching repositories."
      onQueryChange={setQuery}
      onOpenChange={setOpen}
    />
  )
}

function BranchCombo({ projectId, connectorType, repository, value, onChange, testId }: {
  projectId: string
  connectorType: ConnectorType
  repository: string
  value: string
  onChange: (branch: string) => void
  testId: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const { data: branches = [], isLoading } = useProviderRepoBranches(projectId, connectorType, repository, query, open)

  return (
    <RepoSearchCombo
      id={testId}
      testId={testId}
      label="Branch"
      placeholder="Default branch"
      value={value}
      onChange={onChange}
      options={branches.map(b => ({ value: b.name, label: `${b.name}${b.isDefault ? ' (default)' : ''}` }))}
      isLoading={isLoading}
      loadingText="Searching branches..."
      emptyText="No matching branches — type to use a custom branch name."
      onQueryChange={setQuery}
      onOpenChange={setOpen}
    />
  )
}

function FileCombo({ projectId, connectorType, repository, branch, value, onChange, testId, folderPrefix, accept, label }: {
  projectId: string
  connectorType: ConnectorType
  repository: string
  branch: string
  value: string
  onChange: (filePath: string) => void
  testId: string
  folderPrefix?: string
  accept?: string
  label: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const search = query || folderPrefix || ''
  const { data: files = [], isLoading } = useProviderRepoFiles(projectId, connectorType, repository, branch, search, open)

  const acceptExtensions = parseAccept(accept)
  const normalizedFolder = normalizeFolder(folderPrefix)

  const scopedFiles = files.filter(f => {
    if (normalizedFolder && !f.path.toLowerCase().startsWith(`${normalizedFolder.toLowerCase()}/`)) return false
    if (!matchesAccept(f.path, acceptExtensions)) return false
    return true
  })

  return (
    <RepoSearchCombo
      id={testId}
      testId={testId}
      label={label}
      placeholder="Search or type a file path"
      value={value}
      onChange={onChange}
      options={scopedFiles.map(f => ({ value: f.path, label: f.path }))}
      isLoading={isLoading}
      loadingText="Searching files..."
      emptyText="No matching files — type the file path directly."
      onQueryChange={setQuery}
      onOpenChange={setOpen}
    />
  )
}
